import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MmAttachmentModule } from 'mm-web-core/attachment';
import { MmFormsModule } from 'mm-web-core/form';
import { MmI18nModule } from 'mm-web-core/i18n';
import { MmIdentityModule } from 'mm-web-core/identity';
import { MmMenuModule } from 'mm-web-core/menu/menu.module';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzRateModule } from 'ng-zorro-antd/rate';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTreeModule } from 'ng-zorro-antd/tree';
import { NzTreeSelectModule } from 'ng-zorro-antd/tree-select';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSpaceModule } from 'ng-zorro-antd/space';

import { NzUploadModule } from 'ng-zorro-antd/upload';
import { ColorSketchModule } from 'ngx-color/sketch';
import { QuillModule } from 'ngx-quill';

import { MmLanguageModule } from '../mm-web-core/language/language.module';
import {
    AttachmentInputComponent,
    CloseConfirmIconComponent,
    DeleteConfirmIconComponent,
    ImagePreviewComponent,
    ImageUploaderComponent,
    OverlaySpinningComponent,
    PageDataTableComponent,
    ReportViewerComponent,
    ScrapConfirmIconComponent,
} from './components';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { CodeFormComponent } from './components/code-form/code-form.component';
import { CodeTableComponent } from './components/code-table/code-table.component';
import { ColorPickerComponent } from './components/color-picker/color-picker.component';
import { EditIconComponent } from './components/edit-icon/edit-icon.component';
import { LocalizedTextInputComponent } from './components/localized-text-input/localized-text-input.component';
import { MenuListComponent } from './components/menu-list/menu-list.component';
import { ParameterCodeFormComponent } from './components/parameter-code-form/parameter-code-form.component';
import { ParameterCodeTableComponent } from './components/parameter-code-table/parameter-code-table.component';
import { TotalTemplateComponent } from './components/total-template/total-template.component';
import { UserInfoComponent } from './components/user-info/user-info.component';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { FunctionPermissionDirective } from './directives/function-permission.directive';
import { PagedDataDirective } from './directives/paged-data.directive';
import { DynamicSiderWidthDirective } from './directives/dynamic-sider-width.directive';
import { SearchResponsiveUi } from './directives/search-responsive-ui.directive';
import { SoftKeyboardDirective } from './directives/soft-keyboard.directive';
import { AuthenticationInterceptor } from './interceptors/authentication.interceptor';
import { CurrentLanguageInterceptor } from './interceptors/current-language.interceptor';
import {
    AttachmentUrlPipe,
    AutoLocationPipe,
    BoolConvertPipe,
    BoolLabelPipe,
    EmissionFrequencyPipe,
    EnumLabelPipe,
    EnumToChinesePipe,
    FormatMessagePipe,
    GatewayTypePipe,
    LastWordPipe,
    LocalizePipe,
    MaxLenMessagePipe,
    NetworkPipe,
    QrcodeUrlPipe,
    ReponseLevelTextPipe,
    RequiredMessagePipe,
    SafeResourceUrlPipe,
    SafeUrlPipe,
    SubStrPipe,
    ToFixedPipe,
    UserNamePipe,
    CodeValuePipe
} from './pipes';
import { DeviceSelectorComponent } from './components/device-selector/device-selector.component';
import { UploadFileDialogComponent } from './components/upload-file-dialog/upload-file-dialog.component';
import { VariableNameValidatorDirective } from './directives/variable-name.validator.directive';
import { MmCommonModule } from '../mm-web-core/common';
import { ErrorHandlerInterceptor } from '../mm-web-core/common/error-handler.interceptor';
import { MaxOffsetDirectiveDirective } from './directives/max-offset-directive.directive';
import { DateResponsiveUi } from './directives/date-responsive-ui.directive';
import { ButtonResponsiveUi } from './directives/button-responsive-ui.directive';
import { DoubleSearchResponsiveUi, SubDoubleSearchResponsiveUi } from './directives/doubleSearch-responsive-ui.directive';
import { CodeSelectorComponent } from './components/code-selector/code-selector.component';
import { TableSelectorComponent } from './components/table-selector/table-selector.component';
import { ButtonMoreComponent } from './components/button-more/button-more.component';
import { SearchUi } from './directives/search-ui.directive';
import { SearchCollapseComponent } from './components/search-collapse/search-collapse.component';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';


const NGZORRO_MODULES = [
    NzBreadCrumbModule,
    NzButtonModule,
    NzCheckboxModule,
    NzDatePickerModule,
    NzDrawerModule,
    NzDropDownModule,
    NzFormModule,
    NzProgressModule,
    NzCardModule,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzInputNumberModule,
    NzLayoutModule,
    NzMenuModule,
    NzModalModule,
    NzNotificationModule,
    NzPopconfirmModule,
    NzPopoverModule,
    NzRadioModule,
    NzSelectModule,
    NzSliderModule,
    NzSpinModule,
    NzSwitchModule,
    NzTableModule,
    NzTabsModule,
    NzTimePickerModule,
    NzToolTipModule,
    NzTreeModule,
    NzTreeSelectModule,
    NzUploadModule,
    NzRateModule,
    NzSpaceModule,
    NzPaginationModule, 
    NzCarouselModule  
];

const PIPES = [
    AttachmentUrlPipe,
    AutoLocationPipe,
    BoolConvertPipe,
    EmissionFrequencyPipe,
    FormatMessagePipe,
    GatewayTypePipe,
    LastWordPipe,
    LocalizePipe,
    MaxLenMessagePipe,
    NetworkPipe,
    ReponseLevelTextPipe,
    RequiredMessagePipe,
    SafeResourceUrlPipe,
    SafeUrlPipe,
    SubStrPipe,
    ToFixedPipe,
    UserNamePipe,
    QrcodeUrlPipe,
    BoolLabelPipe,
    EnumLabelPipe,
    EnumToChinesePipe,
    CodeValuePipe,
];

const COMPONENTS = [
    AttachmentInputComponent,
    ChangePasswordComponent,
    CloseConfirmIconComponent,
    CodeFormComponent,
    CodeTableComponent,
    ColorPickerComponent,
    DeleteConfirmIconComponent,
    EditIconComponent,
    ImagePreviewComponent,
    ImageUploaderComponent,
    LocalizedTextInputComponent,
    MenuListComponent,
    OverlaySpinningComponent,
    PageDataTableComponent,
    ParameterCodeFormComponent,
    ParameterCodeTableComponent,
    ReportViewerComponent,
    ScrapConfirmIconComponent,
    TotalTemplateComponent,
    UserInfoComponent,
    DeviceSelectorComponent,
    UploadFileDialogComponent,
    CodeSelectorComponent,
    TableSelectorComponent,
    ButtonMoreComponent,
    SearchCollapseComponent,
];

const DIRECTIVES = [
    ClickOutsideDirective,
    FunctionPermissionDirective,
    PagedDataDirective,
    DynamicSiderWidthDirective,
    SearchResponsiveUi,
    SearchUi,
    DoubleSearchResponsiveUi,
    SubDoubleSearchResponsiveUi,
    ButtonResponsiveUi,
    DateResponsiveUi,
    SoftKeyboardDirective,
    VariableNameValidatorDirective,
    MaxOffsetDirectiveDirective
];

const MM_MODULES = [
    MmCommonModule,
    MmFormsModule,
    MmI18nModule,
    MmMenuModule,
    MmLanguageModule,
    MmIdentityModule,
    MmAttachmentModule,
];
const COMMON_MODULE = [
];

const ANGULAR_MODULES = [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
];
const THIRD_PART_MODULES = [
    ColorSketchModule,
    QuillModule,
    TranslateModule,
];
const MODULES = [
    ...ANGULAR_MODULES,
    ...NGZORRO_MODULES,
    ...THIRD_PART_MODULES,
    ...MM_MODULES,
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES,
    ...COMMON_MODULE,
];
const IMPORTS_MODULES = [
    ...ANGULAR_MODULES,
    ...NGZORRO_MODULES,
    ...THIRD_PART_MODULES,
    ...MM_MODULES,
    ...COMMON_MODULE,
];
const DECLARATIONS = [
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES,
];

const PROVIDERS = [
    {
        provide: HTTP_INTERCEPTORS,
        useClass: CurrentLanguageInterceptor,
        multi: true
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthenticationInterceptor,
        multi: true
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorHandlerInterceptor,
        multi: true
    }
];

@NgModule({
    declarations: DECLARATIONS,
    imports: IMPORTS_MODULES,
    exports: MODULES,
    providers: PROVIDERS
})
export class SharedModule {
    static forRoot(): ModuleWithProviders<SharedModule> {
        return {
            ngModule: SharedModule,
        };
    }
}
